import { isEqual } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

interface MultiSelectionPickerContextType<T> {
  setSelectedValues: Dispatch<SetStateAction<T>>;
  contextSelected: T[];
  setContextSelected: Dispatch<SetStateAction<T[]>>;
  fetchAiBestMatch: () => Promise<T>;
  fetchAIData?: () => Promise<T[]>;
  suggestedItems: T[];
  setSuggestedItems: Dispatch<SetStateAction<T[]>>;
  aiState: boolean;
  setAiState: Dispatch<SetStateAction<boolean>>;
  isAiGenerated: (value: T) => boolean;
  disableAll?: boolean;
}

export const MultiSelectionPickerContext = createContext<
  MultiSelectionPickerContextType<any>
>({} as MultiSelectionPickerContextType<any>);

export interface GeneralPurposePickerProps<T> {
  children?: ReactNode;
  selectedValues: T[];
  setSelectedValues: React.Dispatch<React.SetStateAction<T[]>>;
  fetchAIData?: () => Promise<T[]>;
  disableAll?: boolean;
  onChange?: (val: T[]) => void;
  providerMapper?: Map<string, any>;
  selectedKind?: string;
}

export function MultiSelectionPickerContextProvider<T>({
  children,
  selectedValues,
  setSelectedValues,
  fetchAIData,
  disableAll,
  onChange,
  providerMapper,
  selectedKind,
}: GeneralPurposePickerProps<T>) {
  const [suggestedItems, setSuggestedItems] = useState<T[]>([]);
  const [aiState, setAiState] = useState<boolean>(false);
  const [contextSelected, setContextSelected] = useState<T[]>(selectedValues);
  const provider = providerMapper?.get(selectedKind!);
  const context = provider?.useContext();

  const [bestMatchState, fetchAiBestMatch] = useAsyncFn(async () => {
    return fetchAIData ? fetchAIData() : undefined;
  }, [context]);

  const isAiGenerated = useCallback(
    (value: T) => {
      return suggestedItems.some(i => isEqual(i, value));
    },
    [suggestedItems],
  );

  useEffect(() => {
    setContextSelected(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    onChange?.(contextSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextSelected]);

  useEffect(() => {
    if (!aiState) {
      setSuggestedItems([]);
    }
  }, [aiState]);

  useEffect(() => {
    if (!bestMatchState.loading && bestMatchState.value) {
      setSuggestedItems(bestMatchState.value as T[]);
    }
  }, [bestMatchState.loading, bestMatchState.value]);

  return (
    <MultiSelectionPickerContext.Provider
      value={{
        contextSelected,
        setContextSelected,
        setSelectedValues,
        fetchAiBestMatch,
        fetchAIData,
        suggestedItems,
        setSuggestedItems,
        aiState,
        setAiState,
        isAiGenerated,
        disableAll,
      }}
    >
      {children}
    </MultiSelectionPickerContext.Provider>
  );
}

export const useMultiSelectionPickerContext = () =>
  useContext(MultiSelectionPickerContext);
